import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { useSite, useTrans, useTealium, useUtente } from '@next-nx/hooks'
import { Button, FormikField, FormLayout } from '@next-nx/shared-ui/atoms'
import styles from './FormRichiestaPreventivoInPagina.module.sass'
import { Form, Formik } from 'formik'
import { province } from 'libs/shared-ui/utils/province'
import {
  BUDGET_MENSILE_PREVISTO_IVA_ESCLUSA,
  BUDGET_MENSILE_PREVISTO_IVA_INCLUSA,
  KM_PERCORSI,
  TIPOLOGIA_VEICOLO,
  TIPO_UTENTE,
  UTENTE_PRIVATO,
  UTENTE_AZIENDA,
  FLOTTA_POTENZIALE_UTENTE_PRIVATO,
} from 'libs/shared-ui/utils/preventivo'
import {
  isLandingConvenzione,
  getTimestamp,
  isLandingAgenzie,
  isLandingCarrozzerie,
} from '@next-nx/utils'
import { TypDataTracking, LandingType, OffertaType } from '@next-nx/utils/types'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/nextjs'

interface Props {
  onSubmit: (status: string | undefined) => void
  mutation: (e: any) => Promise<any>
  utentePrivato?: boolean
  modelloVeicolo?: string
  tipologiaVeicolo?: string
  className?: string
  offerta?: OffertaType
  id?: string
  utenteCarrozzeria?: string
  setTrackingData: (v: TypDataTracking) => void
}

const FormRichiestaPreventivoInPagina = (props: Props) => {
  const {
    onSubmit = () => {},
    mutation = () => {},
    utentePrivato = true,
    modelloVeicolo = '',
    tipologiaVeicolo = '',
    className = '',
    offerta,
    id = '',
    utenteCarrozzeria = '',
    setTrackingData = () => {},
  } = props
  const t = useTrans()
  const { utente } = useUtente()
  const { landing }: { landing: LandingType } = useSelector((state: any) => state?.landing)
  const router = useRouter()
  const locale = router.locale || landing?.siteId.toString()
  const site = useSite(locale)

  const { linkDataLayer, getProdottoData } = useTealium()

  const [step, setStep] = useState(1)
  const steps = [t('Dati personali'), t('Dati veicolo'), t('Conferma')]
  const [privacyContentVisible, setPrivacyContentVisible] = useState({
    privacy1: false,
    privacy2: false,
    privacy3: false,
    privacy4: false,
  })

  const handleTracking = (step: number, data?: TypDataTracking) => {
    const action_timestamp = getTimestamp()
    let dlObj = {}
    const detail_item = { detail_item: [getProdottoData(offerta, offerta?.categoria?.codice)] }

    switch (step) {
      case 4:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'view',
          action_type: 'thankyou_page',
          action_timestamp,
          action_effect: 'form_richiesta_preventivo_typ',
          info: `chi:${data?.tipoUtente
            ?.replace(/\/|\s+/g, '_')
            .replace(/\./g, '')
            .toLowerCase()}-budget:${data?.budget?.replace(/ /g, '_').toLowerCase()}-km:${data?.km
            ?.replace(/ /g, '_')
            .toLowerCase()}-tipologia:${data?.tipologia?.replace(/ /g, '_').toLowerCase()}${
            data?.convenzione && !!Object.keys(data?.convenzione).length
              ? `-convenzione:${data?.convenzione}`
              : ''
          }`,
          id_preventivo: data?.leadId?.replace(/ /g, '_') || 'none',
          ...(offerta && detail_item),
          position: 'in_page',
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: data?.id_agenzia || '-',
          id_carrozzeria: isLandingCarrozzerie(site) && utenteCarrozzeria ? utenteCarrozzeria : '-',
        }
        break
      case 5:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'click',
          action_type: `chiudi_${data?.stepNome?.replace(/ /g, '_').toLowerCase()}`,
          action_timestamp,
          action_effect: `chiudi_form_richiesta_preventivo_step${data?.step}`,
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: data?.id_agenzia || '-',
          id_carrozzeria: isLandingCarrozzerie(site) && utenteCarrozzeria ? utenteCarrozzeria : '-',
        }
        break
      case 6:
        dlObj = {
          virtual: 'action',
          action_name: 'richiesta_preventivo',
          action_tech: 'click',
          action_type: `${data?.azione?.replace(/ /g, '_').toLowerCase()}_${data?.stepNome
            ?.replace(/ /g, '_')
            .toLowerCase()}`,
          action_timestamp,
          action_effect: `interazione_form_richiesta_preventivo_step${data?.step}`,
          ...(offerta && detail_item),
          market: isLandingCarrozzerie(site) ? 'b2b' : 'b2c',
          id_agenzia: data?.id_agenzia || '-',
          id_carrozzeria: isLandingCarrozzerie(site) && utenteCarrozzeria ? utenteCarrozzeria : '-',
        }
        break
      default:
        dlObj
        break
    }
    linkDataLayer(dlObj)
  }

  const validationSchema = yup.object().shape(
    {
      tipoUtente: yup.string().required('Campo obbligatorio').nullable(),
      nome: yup.string().required('Campo obbligatorio').nullable(),
      cognome: yup.string().required('Campo obbligatorio').nullable(),
      codiceFiscale: yup.string().when('tipoUtente', {
        is: (tipoUtente: string) => tipoUtente === UTENTE_PRIVATO,
        then: yup
          .string()
          .length(16, 'La lunghezza del testo deve essere di 16 caratteri')
          .required('Campo obbligatorio')
          .nullable(),
        otherwise: yup
          .string()
          .length(16, 'La lunghezza del testo deve essere di 16 caratteri')
          .nullable(),
      }),
      ragioneSociale: yup.string().when('tipoUtente', {
        is: (tipoUtente: string) => tipoUtente !== UTENTE_PRIVATO,
        then: yup
          .string()
          .min(2, 'Troppo corto')
          .max(50, 'Troppo lungo')
          .required('Campo obbligatorio')
          .nullable(),
        otherwise: yup.string().nullable(),
      }),
      partitaIva: yup.string().when('tipoUtente', {
        is: (tipoUtente: string) => tipoUtente !== UTENTE_PRIVATO,
        then: yup
          .string()
          .matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici')
          .length(11, 'La lunghezza del testo deve essere di 11 caratteri')
          .required('Campo obbligatorio'),
        otherwise: yup
          .string()
          .matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici')
          .length(11, 'La lunghezza del testo deve essere di 11 caratteri')
          .nullable(),
      }),
      email: yup.string().when('telefono', {
        is: (telefono: string) => !telefono || telefono.length === 0,
        then: yup
          .string()
          .email("Il formato dell'email dev'essere valido")
          .required('Campo obbligatorio')
          .nullable(),
        otherwise: yup.string().email("Il formato dell'email dev'essere valido"),
      }),
      telefono: yup.string().when('email', {
        is: (email: string) => !email || email.length === 0,
        then: yup
          .string()
          .matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici')
          .required('Campo obbligatorio'),
        otherwise: yup.string().matches(/^[0-9]+$/, 'Necessario inserire solo valori numerici'),
      }),
      provincia: yup.string().required('Campo obbligatorio').nullable(),
      flottaPotenziale: yup
        .number()
        .integer('Solo numeri interi')
        .positive('Solo numeri positivi')
        .required('Campo obbligatorio')
        .nullable(),
      convenzione: isLandingConvenzione(site)
        ? yup.string().required('Campo obbligatorio').nullable()
        : yup.string().nullable(),
      agenzia: isLandingAgenzie(site)
        ? yup.string().required('Campo obbligatorio').nullable()
        : yup.string().nullable(),
      budgetMensilePrevisto: yup.string().nullable(),
      tipologiaVeicolo: yup.string().nullable(),
      kmPercorsi: yup.string().nullable(),
      descrizioneRichiesta: yup.string().nullable(),
      informativaPrivacy: yup.bool().oneOf([true], 'Obbligatorio'),
      privacy1: yup.bool(),
      privacy2: yup.bool(),
      privacy3: yup.bool(),
      privacy4: yup.bool(),
    },
    [['email', 'telefono']]
  )

  const showError = (formikProps, name) => {
    return !!formikProps.errors[name]
  }

  const initialValues = {
    tipoUtente: utentePrivato ? UTENTE_PRIVATO : UTENTE_AZIENDA,
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    codiceFiscale: '',
    ragioneSociale: '',
    partitaIva: '',
    provincia: '',
    flottaPotenziale: utentePrivato ? 1 : undefined,
    convenzione: null,
    agenzia: null,
    budgetMensilePrevisto: '',
    tipologiaVeicolo: TIPOLOGIA_VEICOLO.find((t) => t === tipologiaVeicolo) || '',
    kmPercorsi: '',
    descrizioneRichiesta: '',
    informativaPrivacy: false,
    privacy1: false,
    privacy2: false,
    privacy3: false,
    privacy4: false,
  }

  const tipoUtenteOptions = TIPO_UTENTE.map((x) => ({ value: x, label: x }))
  const provinciaOptions = province.map((x) => ({ value: x.nome, label: x.nome }))

  const convenzioniOptions =
    isLandingConvenzione(site) && landing?.convenzioni
      ? landing.convenzioni.map((x) => ({
          value: x,
          label: x,
        }))
      : []
  const agenzieOptions =
    isLandingAgenzie(site) && landing?.agenzie
      ? landing.agenzie.map((x) => ({
          value: x,
          label: x,
        }))
      : []
  const flottaPotenzialeUtentePrivatoOptions = FLOTTA_POTENZIALE_UTENTE_PRIVATO.map((x) => ({
    value: x,
    label: x,
  }))

  return (
    <div className={styles.body} id={id}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, actions) => {
          mutation({
            variables: {
              input: {
                siteId: locale ? parseInt(locale) : 1,
                nome: values?.nome,
                cognome: values?.cognome,
                email: values?.email,
                telefono: values?.telefono,
                provincia: values?.provincia,
                ragioneSociale: values?.ragioneSociale,
                partitaIva: values?.partitaIva,
                codiceFiscale: values?.codiceFiscale,
                flottaPotenziale: values?.flottaPotenziale,
                budgetMensilePrevisto: values?.budgetMensilePrevisto,
                tipologiaVeicolo: values?.tipologiaVeicolo,
                modelloMacchina: modelloVeicolo || '',
                kmPercorsi: values?.kmPercorsi,
                messaggio: values?.descrizioneRichiesta,
                convenzione: values?.convenzione,
                agenzia: values?.agenzia,
                carrozzeria: isLandingCarrozzerie(site) ? utente?.email : null,
                informativaPrivacy: values?.informativaPrivacy,
                privacy1: values?.privacy1,
                privacy2: values?.privacy2,
                privacy3: values?.privacy3,
                privacy4: values?.privacy4,
              },
            },
          })
            ?.then((res) => {
              const status = res?.data?.createRichiestaContatto?.status
              const leadId = res?.data?.createRichiestaContatto?.leadId
              if (status) {
                onSubmit('success')
                const convenzione =
                  !!landing && values?.convenzione ? { convenzione: `${values?.convenzione}` } : {}
                handleTracking(4, {
                  tipoUtente: values?.tipoUtente,
                  budget: values?.budgetMensilePrevisto,
                  km: values?.kmPercorsi,
                  tipologia: values?.tipologiaVeicolo,
                  leadId: leadId,
                  ...convenzione,
                  id_agenzia: values?.agenzia?.split(', ').pop(),
                })
                setTrackingData({
                  id_agenzia: values?.agenzia?.split(', ').pop(),
                  id_carrozzeria:
                    isLandingCarrozzerie(site) && utenteCarrozzeria ? utenteCarrozzeria : '-',
                })
                actions.resetForm()
              }
            })
            .catch((err) => {
              Sentry.captureException(err)
              onSubmit('error')
              actions.setSubmitting(false)
            })
        }}
      >
        {(props) => (
          <Form>
            <div className={styles.head}>
              <div className={styles.legend}>
                {t('Richiedi un preventivo gratuito, un nostro consulente sarà a tua disposizione')}
              </div>
            </div>
            <div className={styles.form}>
              <>
                <FormLayout>
                  {/* Attenzione: stesso div sotto per versione desktop */}
                  {props.values.tipoUtente === UTENTE_PRIVATO && (
                    <div
                      className={`${styles['flottaPotenzialeDisclaimer--mobile']}`}
                      dangerouslySetInnerHTML={{
                        __html: t(
                          '<i>In caso di <strong>flotta potenziale maggiore di 4 veicoli</strong>, compilare il form Business/P.Iva.</i>'
                        ),
                      }}
                    />
                  )}
                  <FormikField
                    formik={props}
                    id="tipoUtente"
                    name="tipoUtente"
                    type="radio"
                    options={tipoUtenteOptions}
                    layout={props.values.tipoUtente === UTENTE_PRIVATO ? undefined : 'full'}
                    required
                    showError={showError}
                    onChange={(e) => {
                      const newTipoUtente = e.target.value
                      props.setFieldValue('tipoUtente', newTipoUtente)
                      if (newTipoUtente === UTENTE_PRIVATO) {
                        props.setFieldValue('partitaIva', '')
                        props.setFieldValue('ragioneSociale', '')
                        props.setFieldValue('flottaPotenziale', 1)
                      } else {
                        props.setFieldValue('codiceFiscale', '')
                      }
                    }}
                  />
                  {/* Attenzione: stesso div sopra per versione mobile */}
                  {props.values.tipoUtente === UTENTE_PRIVATO && (
                    <div
                      className={styles.flottaPotenzialeDisclaimer}
                      dangerouslySetInnerHTML={{
                        __html: t(
                          '<i>In caso di <strong>flotta potenziale maggiore di 4 veicoli</strong>, compilare il form Business/P.Iva.</i>'
                        ),
                      }}
                    />
                  )}
                  <div className={styles.desc}>{t('*Dati obbligatori')}</div>
                  <FormikField
                    formik={props}
                    name="nome"
                    label="Nome"
                    placeholder="Inserisci il tuo nome"
                    required
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    name="cognome"
                    label="Cognome"
                    placeholder="Inserisci il tuo cognome"
                    required
                    showError={showError}
                  />
                  {props.values.tipoUtente === UTENTE_PRIVATO ? (
                    <FormikField
                      formik={props}
                      name="codiceFiscale"
                      label="Codice fiscale"
                      placeholder="Inserisci il tuo codice fiscale"
                      required
                      showError={showError}
                    />
                  ) : (
                    <>
                      <FormikField
                        formik={props}
                        name="partitaIva"
                        label="P.Iva"
                        placeholder="Inserisci la partita iva"
                        required
                        showError={showError}
                      />
                      <FormikField
                        formik={props}
                        name="ragioneSociale"
                        label="Ragione sociale"
                        placeholder="Inserisci la ragione sociale"
                        required
                        showError={showError}
                      />
                    </>
                  )}
                  <FormikField
                    formik={props}
                    name="provincia"
                    label="Provincia"
                    placeholder="Seleziona"
                    type="select"
                    options={provinciaOptions}
                    required
                    showError={showError}
                  />

                  <FormikField
                    formik={props}
                    label="E-mail"
                    name="email"
                    type="email"
                    placeholder="Inserisci l'email"
                    required
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    name="telefono"
                    label="Telefono"
                    placeholder="Inserisci un numero di telefono"
                    required
                    showError={showError}
                  />
                  {!isLandingAgenzie(site) &&
                    (props.values.tipoUtente === UTENTE_PRIVATO ? (
                      <FormikField
                        formik={props}
                        name="flottaPotenziale"
                        label="Flotta potenziale"
                        placeholder="Inserisci la flotta potenziale"
                        type="select"
                        options={flottaPotenzialeUtentePrivatoOptions}
                        showError={showError}
                      />
                    ) : (
                      <FormikField
                        formik={props}
                        name="flottaPotenziale"
                        label="Flotta potenziale"
                        placeholder="Inserisci la flotta potenziale"
                        type="number"
                        min={1}
                        required
                        showError={showError}
                      />
                    ))}
                  {isLandingConvenzione(site) && (
                    <FormikField
                      formik={props}
                      name="convenzione"
                      label="Convenzione"
                      placeholder="Seleziona"
                      type="select"
                      options={convenzioniOptions}
                      required
                      showError={showError}
                    />
                  )}
                  {isLandingAgenzie(site) && (
                    <div className={styles.agenzieFieldContainer}>
                      <FormikField
                        className={styles.agenzieField}
                        formik={props}
                        name="agenzia"
                        label="Agenzia"
                        placeholder="Seleziona"
                        type="select"
                        options={agenzieOptions}
                        required
                        showError={showError}
                        isSearchable={true}
                      />
                      <div className={styles.agenzieFieldText}>
                        {t('Puoi cercare per ragione sociale, comune, provincia e codice agenzia')}
                      </div>
                    </div>
                  )}
                  <FormikField
                    formik={props}
                    id="informativaPrivacy"
                    name="informativaPrivacy"
                    elementLabel={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: t(
                            "Ho preso visione dell'{0}informativa privacy{1} e autorizzo all’invio di comunicazioni inerenti la mia richiesta*",
                            [
                              '<a href="https://www.unipolrental.it/privacy-policy" target="_blank">',
                              '</a>',
                            ]
                          ),
                        }}
                      />
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy1"
                    name="privacy1"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento al trattamento dei miei dati personali per finalità commerciali e di marketing da parte di UnipolRental'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy1'] ? (
                          <>
                            {t(
                              'Invio di comunicazioni commerciali relative a servizi e/o prodotti di UnipolRental (basate anche sull’utilizzo del veicolo e sui dati rilevati dal/i Dispositivo/i se installato/i), nonché relative ad ulteriori prodotti e/o servizi di UnipolSai, delle altre società del Gruppo Unipol e/o di terzi, attraverso materiale pubblicitario, newsletter, vendita diretta, compimento di ricerche di mercato o di comunicazione commerciale da parte di UnipolRental mediante modalità di contatto automatizzate (come SMS, MMS, fax, chiamate telefoniche automatizzate, posta elettronica, messaggi su applicazioni web) e tradizionali (come posta cartacea e chiamate telefoniche con operatore)'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy1: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy1: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy2"
                    name="privacy2"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento al trattamento dei miei dati personali per finalità di profilazione da parte di UnipolRental'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy2'] ? (
                          <>
                            {t(
                              'Attività di analisi, compiute da UnipolRental, dei dati acquisiti all’inizio e nel corso dei rapporti contrattuali con UnipolRental, anche con riguardo ai servizi e prodotti fruiti, basate anche sull’utilizzo del/i veicolo/i - ove disponibili valutazione di alcuni dati legati alla localizzazione, alle percorrenze, agli stili di guida associabili al veicolo assicurato e alle aree geografiche attraversate -, attraverso anche elaborazioni elettroniche, di comportamenti ed esigenze dell’Interessato per migliorare e sviluppare i servizi forniti ed individuare le offerte più vantaggiose ed i prodotti di maggior interesse dell’Interessato stesso'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy2: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy2: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy3"
                    name="privacy3"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento all’invio dei miei dati personali per finalità di marketing a Società del Gruppo Unipol'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy3'] ? (
                          <>
                            {t(
                              'Comunicazione dei dati personali alle altre Società del Gruppo Unipol, che li potranno trattare per l’invio di comunicazioni commerciali relative a servizi assicurativi, nonché a ulteriori prodotti e/o servizi di dette Società e/o di terzi (basate anche sull’utilizzo del veicolo e sui dati rilevati dal/i Dispositivo/i se installato/i), attraverso l’invio di materiale pubblicitario, newsletter, vendita diretta, compimento di ricerche di mercato o di comunicazione commerciale mediante tecniche di comunicazione a distanza con modalità automatizzate (come SMS, MMS, fax, chiamate telefoniche automatizzate, posta elettronica, messaggi su applicazioni web), nonché tradizionali (come posta cartacea e chiamate telefoniche con operatore)'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy3: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy3: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <FormikField
                    formik={props}
                    id="privacy4"
                    name="privacy4"
                    elementLabel={
                      <>
                        {t(
                          'Acconsento all’invio dei miei dati personali per finalità di profilazione a Società del Gruppo Unipol'
                        )}{' '}
                        -{' '}
                        {privacyContentVisible['privacy4'] ? (
                          <>
                            {t(
                              'Comunicazione dei dati personali da UnipolRental alle altre società del Gruppo Unipol, che li utilizzeranno per ulteriori attività di analisi, anche attraverso elaborazioni elettroniche, di comportamenti ed esigenze dell’Interessato, basate sulla valutazione di alcuni dati legati appunto alle percorrenze aggregate e/o puntuali (ove disponibili), alle aree geografiche attraversate, alle esigenze e alle abitudini, dell’Interessato stesso, al fine di migliorare e sviluppare i servizi ed i prodotti forniti ed individuare le offerte più vantaggiose e proporre, sulla base di tali dati, i prodotti ed i servizi di maggiore interesse per l’Interessato'
                            )}{' '}
                            -{' '}
                            <a
                              onClick={(e) => {
                                e.preventDefault(),
                                  setPrivacyContentVisible({
                                    ...privacyContentVisible,
                                    privacy4: false,
                                  })
                              }}
                            >
                              {t('Leggi di meno')}
                            </a>
                          </>
                        ) : (
                          <a
                            onClick={(e) => {
                              e.preventDefault(),
                                setPrivacyContentVisible({
                                  ...privacyContentVisible,
                                  privacy4: true,
                                })
                            }}
                          >
                            {t('Leggi di più')}
                          </a>
                        )}
                      </>
                    }
                    required
                    type="checkbox"
                    layout="full"
                    showError={showError}
                  />
                  <div className={styles.submitButton}>
                    <Button
                      label={t('Prosegui')}
                      labelMobile={t('Prosegui')}
                      onClick={() => {
                        props.handleSubmit()
                      }}
                      disabled={!props.isValid}
                      loading={props.isSubmitting}
                    />
                  </div>
                </FormLayout>
              </>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default FormRichiestaPreventivoInPagina
